import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { uploadDocAttachment } from "../../../api/papers";
import FileTreePicker from "../papers/FileTreePicker";

/**
 * MobileAttachmentBox
 * - 모바일 전용 문서 첨부 컴포넌트 (버튼 1행 배치)
 * - UI 모바일 대응
 *
 * Props
 *  - initial?: Item[]
 *
 * Ref API
 *  - save(dns: string): Promise<void>
 *  - getItems(): Item[]
 *  - clear(): void
 */
const MobileAttachmentBox = forwardRef(({ initial = [] }, ref) => {
  const [items, setItems] = useState(initial);
  const [openLibrary, setOpenLibrary] = useState(false);
  const fileInputRef = useRef(null);

  /**
   * ✅ 저장 로직
   * 문서에 첨부된 항목들(자료실+로컬)을 백엔드로 업로드 또는 연결
   */
  const save = async (dns) => {
    if (!dns) throw new Error("dns가 필요합니다.");

    for (const it of items) {
      try {
        if (it._type === "library") {
          await uploadDocAttachment(dns, { file_ids: [it.id] });
        } else if (it._type === "local") {
          await uploadDocAttachment(dns, { file: it.file, name: it.name });
        }
      } catch (err) {
        console.error("❌ 첨부 실패:", it.name, err);
      }
    }
  };

  // ✅ 외부에서 제어 가능한 API 등록
  useImperativeHandle(ref, () => ({
    save,
    getItems: () => items,
    clear: () => setItems([]),
    setItems: (newItems) => setItems(newItems),
  }));

  // ✅ 로컬 파일 첨부
  const handleLocalChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const mapped = files.map((f) => ({
      _type: "local",
      file: f,
      name: f.name,
    }));

    setItems((prev) => [...prev, ...mapped]);
    e.target.value = ""; // 같은 파일 재선택 허용
  };

  // ✅ 삭제
  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  // ✅ 자료실 선택 확정
  const handleConfirmLibrary = (files) => {
    const mapped = files.map((f) => ({
      _type: "library",
      id: f.id,
      name: f.name,
      ...f,
    }));

    setItems((prev) => {
      const existing = new Set(
        prev.filter((it) => it._type === "library").map((it) => it.id)
      );
      const newOnes = mapped.filter((f) => !existing.has(f.id));
      return [...prev, ...newOnes];
    });
  };

  return (
    <div className="space-y-3">
      {/* 첨부 버튼 영역 – 모바일 최적화: 1:1 비율 그리드 */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className="flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 py-2.5 text-[13px] font-medium text-slate-700 shadow-sm active:scale-[0.97] transition-transform whitespace-nowrap"
          onClick={() => setOpenLibrary(true)}
        >
          자료실에서 선택
        </button>

        <button
          type="button"
          className="flex items-center justify-center rounded-xl border border-slate-200 bg-white py-2.5 text-[13px] font-medium text-slate-700 shadow-sm active:scale-[0.97] transition-transform whitespace-nowrap"
          onClick={() => fileInputRef.current?.click()}
        >
          파일 첨부
        </button>

        {/* 로컬 파일 선택 인풋 – 화면에는 보이지 않도록 처리 */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleLocalChange}
        />
      </div>

      {/* 첨부 목록 – 있으면 카드형 리스트로 정리 */}
      {items.length > 0 && (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {items.map((it, i) => (
            <li
              key={`${it._type}-${i}`}
              className="flex items-center justify-between px-3.5 py-2.5 text-[13px]"
            >
              <div className="min-w-0">
                <span className="truncate text-slate-800">{it.name}</span>
                {it._type === "local" && (
                  <em className="ml-1 text-[11px] text-slate-400">(로컬)</em>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeItem(i)}
                className="text-[12px] text-slate-500 hover:text-red-500"
              >
                삭제
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* 📁 자료실 선택 모달 */}
      <FileTreePicker
        open={openLibrary}
        onClose={() => setOpenLibrary(false)}
        selectionMode="multiple"
        onConfirm={handleConfirmLibrary}
      />
    </div>
  );
});

MobileAttachmentBox.displayName = "MobileAttachmentBox";

export default MobileAttachmentBox;

